type Listener = () => void;

export type StorageNamespace = "sync" | "local";
export type StorageArea = Record<string, any>;

export type Storage = {
    [key in StorageNamespace]: StorageArea
}

// Helper to check if a key matches a pattern
function matchesPattern(key: string, pattern: string): boolean {
    // Convert pattern to regex (e.g., "auth_.*" -> /^auth_.*$/)
    try {
        const regex = new RegExp(pattern);
        const result = regex.test(key);
        return result;
    } catch (e) {
        return false;
    }
}

// This is a "Factory" function. It creates a closure to hold state.
export function createCommunicationStore(initialState: Storage = { "sync": {}, "local": {} }) {
    let settings = initialState;
    // Map of patterns to their listener sets
    const listeners : Record<string, Set<any>> = {};

    // Helper to notify listeners that match the changed keys
    const notifyListeners = (changedKeys: string[]) => {
        const notifiedListeners = new Set<Listener>();

        for (const [pattern, listenerSet] of Object.entries(listeners)) {
            changedKeys.forEach(key => {
                if (!matchesPattern(key, pattern)) return;
                listenerSet.forEach(listener => {
                    if (notifiedListeners.has(listener)) return;
                    notifiedListeners.add(listener);
                    listener();
                });
            });
        }
    };

    return {
        // Get current value (cheap)
        getSnapshot: () => settings,

        initListeners: () => {
            Object.values(listeners).forEach((listenerSet, pattern) => {
                listenerSet.forEach(listener => {
                    listener();
                });
            });
        },

        // Subscribe function with pattern support
        subscribe: (listener: Listener, pattern: string = ".*") => {
            if (!listeners[pattern]) {
                listeners[pattern] = new Set();
            }
            listeners[pattern].add(listener);

            return () => {
                const listenerSet = listeners?.[pattern];
                if (listenerSet) {
                    listenerSet.delete(listener);
                    // Clean up empty pattern entries
                    if (listenerSet.size === 0) {
                        delete listeners[pattern];
                    }
                }
            };
        },

        unsubscribe: (listener: Listener, pattern: string = ".*") => {
            const listenerSet = listeners?.[pattern];
            if (listenerSet) {
                listenerSet.delete(listener);
                // Clean up empty pattern entries
                if (listenerSet.size === 0) {
                    delete listeners[pattern];
                }
            }

        },

        // Update function
        setValue: (key: string, value: any) => {
            // 1. Update the internal state immutably
            settings = { ...settings, [key]: value };
            // 2. Notify matching listeners
            notifyListeners([key]);
        },

        // Set the full settings object and compare for changes
        setSettings: (newSettings: Storage) => {
            // Compare old and new settings to detect changes using deep equality
            const changedKeys: string[] = [];

            for (const key in newSettings) {
                if (JSON.stringify(settings[key]) !== JSON.stringify(newSettings[key])) {
                    changedKeys.push(key);
                }
            }

            // Check for removed keys
            for (const key in settings) {
                if (!(key in newSettings)) {
                    changedKeys.push(key);
                }
            }

            console.log("[commms] changedKeys", changedKeys);

            // Only update and notify if something actually changed
            if (changedKeys.length > 0) {
                settings = newSettings;
                notifyListeners(changedKeys);
            }

            return changedKeys;
        },

        updateSettings: (namespace: StorageNamespace, newSettings: StorageArea) => {
            for (const key in newSettings) {
                if (!settings[namespace]) return;
                settings[namespace][key] = newSettings[key];
            }
            notifyListeners(Object.keys(newSettings));
        }
    };
}

// Define the Type of the object returned above for TypeScript
export type CommunicationStore = ReturnType<typeof createCommunicationStore>;